import { liftFlatToFloor } from "../_qa/topup-merge";
import { DD_PRIVACY_NOTE, DD_REVIEWED_TH, DD_SCOPE_NOTE, DD_TOPICS, type DueDiligenceTopic } from "./index";

export type DueDiligenceQA = {
  slug: string;
  q: string;
  a: string;
  topicSlug: string;
  synonyms: string[];
  indexable: boolean;
  /** Short search title that keeps the question angle visible when truncated. */
  title?: string;
};

const CORE_LABELS: Record<string, string> = { what: "คืออะไร", documents: "เอกสารที่ต้องใช้", steps: "ขั้นตอน", sources: "แหล่งตรวจทางการ", privacy: "PDPA", limits: "ข้อจำกัดของผลตรวจ", warnings: "ข้อควรระวัง", update: "ควรตรวจซ้ำเมื่อใด" };

const CONTEXTS = ["ก่อนลงทุน", "ก่อนซื้อกิจการ", "ก่อนร่วมทุน", "ก่อนให้เครดิต", "ก่อนเซ็นสัญญา", "ก่อนโอนเงิน", "ก่อนแต่งตั้งตัวแทน", "ก่อนรับซัพพลายเออร์", "ก่อนซื้ออสังหาริมทรัพย์", "ก่อนจ้างผู้บริหาร", "สำหรับนักลงทุนต่างชาติ", "สำหรับธุรกรรมข้ามประเทศ", "เมื่อข้อมูลสองแหล่งไม่ตรงกัน", "เมื่อคู่สัญญาเพิ่งเปลี่ยนกรรมการ", "เมื่อมีชื่อบุคคลคล้ายกัน", "เมื่อเอกสารใกล้หมดอายุ", "เมื่อค้นไม่พบข้อมูล", "เมื่อคู่สัญญาไม่ส่งเอกสาร", "เมื่อธุรกรรมมีหลายบริษัท", "ก่อนวันปิดธุรกรรม"];
const ASPECTS = ["ควรเริ่มจากอะไร", "ต้องใช้เอกสารอะไร", "ตรวจจากแหล่งใด", "มีขั้นตอนอย่างไร", "ต้องขอความยินยอมหรือไม่", "มีข้อจำกัดอะไร", "ควรตรวจซ้ำเมื่อใด", "ผลตรวจใช้ตัดสินใจอย่างไร"];

function core(topic: DueDiligenceTopic): DueDiligenceQA[] {
  const rows: Array<[string, string, string]> = [
    ["what", `${topic.nameTh} คืออะไร`, `${topic.summaryTh} ${DD_SCOPE_NOTE}`],
    ["documents", `${topic.nameTh} ต้องใช้เอกสารอะไร`, `เอกสารหลักประกอบด้วย ${topic.documentsTh.join(" · ")} รายการจริงต้องปรับตามสิทธิในการเข้าถึงข้อมูลและความเสี่ยงของธุรกรรม ${DD_PRIVACY_NOTE}`],
    ["steps", `${topic.nameTh} มีขั้นตอนอย่างไร`, `ขั้นตอนหลักคือ ${topic.stepsTh.map((step, index) => `${index + 1}) ${step.title}: ${step.detail}`).join(" ")} ${DD_SCOPE_NOTE}`],
    ["sources", `${topic.nameTh} ควรตรวจจากแหล่งใด`, `ควรเริ่มจากแหล่งต้นทาง ได้แก่ ${topic.authoritySites.join(" และ ")} แล้วเทียบกับเอกสารที่คู่สัญญาเปิดเผย บันทึกวันที่ตรวจและตัวระบุที่ใช้ค้นทุกครั้ง ${topic.citationTh}`],
    ["privacy", `${topic.nameTh} ต้องคำนึงถึง PDPA อย่างไร`, `${DD_PRIVACY_NOTE} ต้องจำกัดผู้เข้าถึง ระยะเวลาเก็บ และการส่งต่อรายงาน พร้อมมีกระบวนการแก้ไขข้อมูลที่คลาดเคลื่อน`],
    ["limits", `${topic.nameTh} รับประกันว่าไม่มีความเสี่ยงได้หรือไม่`, `ไม่ได้ ${DD_SCOPE_NOTE} การตรวจช่วยลดความไม่แน่นอนจากข้อมูลที่เข้าถึงได้ แต่ไม่แทนคำรับรองของคู่สัญญา เงื่อนไขคุ้มครองในสัญญา หรือการทบทวนโดยผู้เชี่ยวชาญ`],
    ["warnings", `${topic.nameTh} มีข้อควรระวังอะไร`, `ข้อควรระวังสำคัญคือ ${topic.warningsTh.join(" · ")} ${DD_PRIVACY_NOTE}`],
    ["update", `${topic.nameTh} ควรตรวจซ้ำเมื่อใด`, `ควรตรวจซ้ำใกล้วันลงนามและวันชำระเงิน รวมทั้งเมื่อมีการเปลี่ยนกรรมการ ผู้ถือหุ้น ใบอนุญาต เอกสารสิทธิ หรือข้อเท็จจริงสำคัญ เพราะรายงานสะท้อนสถานะ ณ วันที่ตรวจเท่านั้น ข้อมูล ณ ${DD_REVIEWED_TH}`],
  ];
  return rows.map(([suffix, q, a]) => ({ slug: `${topic.slug}-${suffix}`, q, a, title: `${CORE_LABELS[suffix]}: ${topic.nameTh}`, topicSlug: topic.slug, synonyms: [topic.nameEn.toLowerCase(), `${topic.nameTh} ${suffix}`], indexable: true }));
}

function matrix(topic: DueDiligenceTopic): DueDiligenceQA[] {
  const rows: DueDiligenceQA[] = [];
  CONTEXTS.forEach((context, contextIndex) => {
    ASPECTS.forEach((aspect, aspectIndex) => {
      const step = topic.stepsTh[aspectIndex % topic.stepsTh.length];
      const document = topic.documentsTh[contextIndex % topic.documentsTh.length];
      rows.push({
        slug: `${topic.slug}-case-${contextIndex + 1}-${aspectIndex + 1}`,
        q: `${topic.nameTh} ${context} ${aspect}`,
        a: `${context} ควรกำหนดขอบเขตให้ตรงกับความเสี่ยงของธุรกรรม เริ่มจาก “${step.title}” โดย${step.detail} เอกสารที่ควรขอเป็นลำดับแรกคือ ${document} จากนั้นเทียบกับ ${topic.authoritySites.join(" และ ")} และบันทึกข้อจำกัดของผลค้น ${DD_PRIVACY_NOTE} ${DD_SCOPE_NOTE}`,
        topicSlug: topic.slug,
        synonyms: [`${topic.nameEn.toLowerCase()} ${context}`, `${topic.nameTh} ${aspect}`],
        indexable: false,
      });
    });
  });
  return rows;
}

export const DD_QA_ALL: DueDiligenceQA[] = DD_TOPICS.flatMap((topic) => [...core(topic), ...matrix(topic)]);

liftFlatToFloor(DD_QA_ALL, {
  prefix: "dd-t7",
  asOf: `ข้อมูลทบทวน ณ ${DD_REVIEWED_TH} ${DD_SCOPE_NOTE} ${DD_PRIVACY_NOTE}`,
  authorityTh: "หน่วยงานทะเบียนและฐานข้อมูลราชการที่เกี่ยวข้อง",
  clusterTh: "งานตรวจสอบสถานะธุรกิจ (Due Diligence)",
  primary: DD_TOPICS.map((t) => ({ slug: t.slug, th: t.nameTh, en: t.nameEn })),
  extra: () => ({ indexable: false }),
});
export const DD_QA_INDEXABLE: DueDiligenceQA[] = DD_QA_ALL.filter((qa) => qa.indexable);
export const DD_QA_BY_SLUG: Record<string, DueDiligenceQA> = Object.fromEntries(DD_QA_ALL.map((qa) => [qa.slug, qa]));